import { useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button, ErrorAlert, Field, Textarea } from './ui';

/**
 * Confirmation with an optional or required note, used for dismiss, reject,
 * send back, unlock and re-export. `onConfirm` may throw; the error is shown.
 */
export function PromptDialog({
  title,
  description,
  label = 'Note',
  required = false,
  minLength = 1,
  confirmLabel = 'Confirm',
  danger,
  hideNote,
  onConfirm,
  onClose,
}: {
  title: string;
  description?: ReactNode;
  label?: string;
  required?: boolean;
  minLength?: number;
  confirmLabel?: string;
  danger?: boolean;
  hideNote?: boolean;
  onConfirm: (note: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const ok = hideNote || !required || note.trim().length >= minLength;

  const submit = async () => {
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(note.trim());
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant={danger ? 'danger-solid' : 'primary'} loading={busy} disabled={!ok}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        {description && <div>{description}</div>}
        <ErrorAlert error={error} />
        {!hideNote && (
          <Field
            label={label}
            required={required}
            hint={required && minLength > 1 ? `At least ${minLength} characters` : required ? 'Required' : 'Optional'}
          >
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus />
          </Field>
        )}
      </div>
    </Modal>
  );
}
